/* eslint-disable @typescript-eslint/default-param-last */
import { AxiosInstance } from 'axios';
import FileSaver from 'file-saver';
import camelcaseKeys from 'camelcase-keys';
import { IDrives, IDrivesResponse, IFilterResponse } from '@common/interfaces/LogbookAPI';
import {
  GetAllDrivesParamsType,
  IDrivesRepository,
  SortingDirections,
} from '@common/interfaces/LogbookRepository';
import { LocalStorageKeys, SessionStorageKeys } from '@common/constants';
import AxiosRepository from './axios.repository';

class DrivesRepository extends AxiosRepository implements IDrivesRepository {
  public constructor(axiosClient: AxiosInstance) {
    super(axiosClient);
  }

  private transformData(data: Record<string, any>): any {
    return camelcaseKeys(data, { deep: true });
  }

  public async getAllDrives(
    facilityId: string,
    isMultiAgency: boolean,
    page: number,
    perPage: number,
    sorting: { driveTime: SortingDirections },
    params?: GetAllDrivesParamsType,
  ): Promise<IDrivesResponse | null> {
    const aggregatedParams = { ...params, additionalFilter: undefined };

    const { data } = await this.get(`facilities/${facilityId}/mobility/logbook/drives/`, {
      params: {
        page,
        per_page: perPage,
        ordering: sorting.driveTime === 'asc' ? 'driveTime' : '-driveTime',
        ...aggregatedParams,
        ...params?.additionalFilter,
        agency_short_name: isMultiAgency
          ? sessionStorage.getItem(SessionStorageKeys.SELECTED_AGENCY)
          : undefined,
      },
    });

    const updatedData = this.transformData(data);

    return this.transformData({
      ...updatedData,
      items: updatedData.results,
      hasNext: !!updatedData.next,
      aggregatedInfo: {
        doneDrivesNumber: updatedData.doneDrivesNumber,
        toDoDrivesNumber: updatedData.toDoDrivesNumber,
      },
    });
  }

  public async getDrivesCsv(
    facilityId: string,
    isMultiAgency: boolean,
    location: string,
    sorting: { driveTime: SortingDirections },
    params?: GetAllDrivesParamsType,
  ): Promise<void> {
    const aggregatedParams = { ...params, additionalFilter: undefined };
    const { data } = await this.get(`facilities/${facilityId}/mobility/logbook/drives/export/`, {
      params: {
        ordering: sorting.driveTime === 'asc' ? 'driveTime' : '-driveTime',
        location,
        ...aggregatedParams,
        ...params?.additionalFilter,
        agency_short_name: isMultiAgency
          ? sessionStorage.getItem(SessionStorageKeys.SELECTED_AGENCY)
          : undefined,
      },
    });

    const filename = `${location?.replaceAll('|', '_')}_${params?.from_date}-${
      params?.to_date
    }.csv`;

    FileSaver.saveAs(new Blob([this.transformData(data)], { type: 'text/csv' }), filename);
  }

  public async getDriveById(facilityId: string, id: string): Promise<IDrives> {
    const { data } = await this.get(`facilities/${facilityId}/mobility/logbook/drives/${id}/`);
    return this.transformData(data);
  }

  public async getFilters(
    facilityId: string,
    to_do_drives = false,
    to_date: string,
    from_date: string,
  ): Promise<IFilterResponse> {
    const { data } = await this.get(
      `facilities/${facilityId}/mobility/logbook/drives/filter-data/`,
      {
        params: {
          location: localStorage.getItem(LocalStorageKeys.LOCATION),
          to_do_drives,
          to_date,
          from_date,
        },
      },
    );
    return data;
  }
}

export default DrivesRepository;
